import { FC, useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { TopNavigationInvestor } from '~/@biz_investor/navigation-top/TopNavigationInvestor'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLoggedInUser } from '~/@biz_account/useLoggedInUser/useLoggedInUser'
import { UserProfile } from '~/@biz_common/user-profile/UserProfile/UserProfile'
import {
  IContract,
  IContractsGroup,
} from '~/@api/generated-api-types/global.types'
import useOpportunityStore from '~/@biz_investor/useOpportunityStore/useOpportunityStore'
import { getApi } from '~/@utils/api/getApi'
import { useAlert } from '~/@ui/alert/useAlert'
import { ViewDocumentModal } from '~/@biz_investor/sign-contracts/ViewDocumentModal/ViewDocumentModal'
import { Document } from '~/@ui/document/Document'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { TopNavigationOwner } from '~/@biz_owner/navigation-top/TopNavigationOwner'
import { useCurrentUserData } from '~/@biz_account/useCurrentUserData/useCurrentUserData'
import { Header } from '~/@ui/header/Header'

export const UserProfilePage: FC = () => {
  const { showAlert } = useAlert()
  const { clearLoggedInUser } = useLoggedInUser()
  const { user } = useCurrentUserData()
  const { clearOpportunity } = useOpportunityStore()

  const navigate = useNavigate()
  const location = useLocation()

  const isInvestorRoute = location.pathname.includes('/investor/')
  const isOwnerRoute = location.pathname.includes('/vlastnik/')

  const [documents, setDocuments] = useState<IContractsGroup[] | null>(null)

  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    title: '',
    body: '',
  })

  const mainContract = documents?.find((con) => con.type === 'investor_main')
    ?.contracts[0]

  const closeDocumentModal = () =>
    setDocumentModal({ isOpen: false, title: '', body: '' })

  const getDocuments = async () => {
    try {
      const { data: response } =
        await getApi().investorContractsAppApiControllerGetInvestmentOrders()

      if ('success' in response) {
        setDocuments(response.contracts)
        return
      }

      throw new Error('Nasatala neznáma chyba pri načítavaní náhľadov zmlúv.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const downloadPdf = async (contract: IContract) => {
    try {
      const response =
        await getApi().investorContractsAppApiControllerGenerateSignaleDraftPdf(
          contract.id
        )

      const pdfData = await response.arrayBuffer()
      const blob = new Blob([pdfData], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${contract.type}-${contract.investmentProductId}.pdf`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)

      showAlert('success', 'Sťahovannie prebehlo úspešne.')
    } catch (err) {
      console.error(err)
      showAlert('error', 'Nastala chyba.')
    }
  }

  const logout = () => {
    clearLoggedInUser()
    navigate('/account')
  }

  useEffect(() => {
    clearOpportunity()
    getDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack className="bg-zBlack200">
      {isInvestorRoute && <TopNavigationInvestor user={user} />}
      {isOwnerRoute && <TopNavigationOwner user={user} />}{' '}
      <Stack
        className="w-full max-w-[768px] self-center pt-6 pb-28 rounded-t-3xl bg-zWhite100"
        sx={{ minHeight: 'calc(100vh - 70px)' }}>
        <Header
          title="Používateľský profil"
          text="Správa používateľského profilu."
          className="px-6 mb-8"
        />

        {mainContract && (
          <Stack className="gap-4 px-6 pb-4">
            <Stack className="flex-row justify-between items-center">
              <Typography className="text-base font-bold text-zBlack200">
                Dokumenty
              </Typography>
              <Link
                to="/investor/user-profile/documents"
                className="text-sm font-bold text-zBlack200 underline underline-offset-2">
                Zobraziť všetky dokumenty
              </Link>
            </Stack>

            <Stack>
              <Document
                title={mainContract.title}
                onClickView={() => {
                  setDocumentModal({
                    isOpen: true,
                    title: mainContract.title,
                    body: mainContract.content,
                  })
                }}
                onClickDownload={() => downloadPdf(mainContract)}
              />
              <Stack className="h-2.5 mx-6 bg-[#EBEBFF] rounded-b-2d" />
              <Stack className="h-2.5 mx-12 bg-[#F5F5FF] rounded-b-2d" />
            </Stack>
          </Stack>
        )}

        <UserProfile
          user={user}
          logout={logout}
          chanePhone={() =>
            navigate(
              isInvestorRoute
                ? '/investor/user-profile/change-phone'
                : '/vlastnik/user-profile/change-phone'
            )
          }
        />
      </Stack>
      <ViewDocumentModal
        isOpen={documentModal.isOpen}
        handleClose={closeDocumentModal}
        title={documentModal.title}
        body={documentModal.body}
      />
    </Stack>
  )
}
